import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { FundraisingResponse } from '@shared/schemas';

import {
  draftFromRound,
  type FundraisingFieldChange,
} from '../../_shared/domain/fundraising-draft.model';
import type { FundraisingFormError } from '../../_shared/domain/fundraising-form-error';
import { makeInitialState } from './edit-fundraising.state';

/**
 * Edit-fundraising slice. Reducers are pure state transitions only; the I/O
 * (load / update) lives in the saga, which dispatches these lifecycle
 * transitions as it progresses.
 */
const slice = createSlice({
  name: 'editFundraising',
  initialState: makeInitialState,
  reducers: {
    // ── Load lifecycle (dispatched by the saga) ──
    loadStarted: (state) => {
      state.loadStatus = 'loading';
      state.error = null;
    },
    /** Hydrate the draft from the loaded round (numeric amount → field text). */
    loadSucceeded: (state, action: PayloadAction<FundraisingResponse>) => {
      const fundraising = action.payload;
      state.fundraising = fundraising;
      state.draft = draftFromRound(fundraising);
      state.loadStatus = 'loaded';
    },
    loadFailed: (state, action: PayloadAction<FundraisingFormError>) => {
      state.loadStatus = 'failed';
      state.error = action.payload;
    },
    /** A single field changed; clears that field's validation error. */
    fieldChanged: (state, action: PayloadAction<FundraisingFieldChange>) => {
      const change = action.payload;
      if (change.field === 'amount') state.draft.amount = change.value;
      else if (change.field === 'currency') state.draft.currency = change.value;
      else if (change.field === 'closeMonth') state.draft.closeMonth = change.value;
      else state.draft.closeYear = change.value;
      delete state.validationErrors[change.field];
    },
    // ── Save lifecycle ──
    saveStarted: (state) => {
      state.saveStatus = 'saving';
      state.error = null;
      state.validationErrors = {};
    },
    saveSucceeded: (state) => {
      state.saveStatus = 'succeeded';
    },
    saveFailed: (state, action: PayloadAction<FundraisingFormError>) => {
      state.saveStatus = 'failed';
      state.error = action.payload;
      if (action.payload.kind === 'validation') {
        state.validationErrors = action.payload.fields;
      }
    },
    /** Reset everything to a pristine state (on mount / unmount). */
    resetForm: () => makeInitialState(),
    /**
     * Edit a round the page already holds (the card's inline editor, the Edit
     * dialog): no GET, the list row is the source.
     */
    formOpened: (_state, action: PayloadAction<FundraisingResponse>) => ({
      ...makeInitialState(),
      fundraising: action.payload,
      draft: draftFromRound(action.payload),
      loadStatus: 'loaded' as const,
    }),
    // ── Delete lifecycle (dispatched by the saga) ──
    deleteStarted: (state) => {
      state.deleteStatus = 'saving';
      state.deleteError = null;
    },
    deleteSucceeded: (state) => {
      state.deleteStatus = 'succeeded';
    },
    deleteFailed: (state, action: PayloadAction<FundraisingFormError>) => {
      state.deleteStatus = 'failed';
      state.deleteError = action.payload;
    },
    // ── Revert lifecycle (the "Undo" of an update) ──
    revertStarted: (state) => {
      state.revertStatus = 'saving';
    },
    revertSucceeded: (state) => {
      state.revertStatus = 'succeeded';
    },
    revertFailed: (state) => {
      state.revertStatus = 'failed';
    },
  },
});

export const editFundraisingActions = slice.actions;
export const editFundraisingReducer = slice.reducer;
